import { API } from '../Auth';

// ---------------------------------------------------------------------------
// The Integrations UI retains its legacy field shape (name/location/port/
// username/password/enginecode/enginetype). The new backend at
// /api/integrations/** expects displayName/baseUrl/authType/... — this file
// owns the translation so the rest of the UI doesn't need to change.
//
// Mapping:
//   UI.name             → backend.displayName
//   UI.enginecode       → backend.registeredServiceId   (lowercased)
//   UI.location + port  → backend.baseUrl               (synthesized if needed)
//   UI.username         → backend.username
//   UI.password         → backend.password              (null = don't change)
//   UI.enabled          → backend.enabled               (default true)
//
// Supported integrations for this release are enumerated client-side — the
// service-registration.json file drives which ids are valid; DELPHIX_MASKING
// is the only 3rd-party service shipping in this release.
// ---------------------------------------------------------------------------

const API_ENDPOINT = 'api';
const INTEGRATIONS_PATH = '/api/integrations';

const SUPPORTED_INTEGRATIONS = [
  {
    enginecode: 'DELPHIX_MASKING',
    enginetype: 'MASKING',
    registeredServiceId: 'integrations-delphix',
    name: 'Delphix Masking',
  },
];

// ---- helpers ----

const toBaseUrl = (integration) => {
  if (integration.baseUrl && integration.baseUrl.trim().length > 0) {
    return integration.baseUrl;
  }
  if (!integration.location) return '';
  const proto = /^https?:\/\//i.test(integration.location) ? '' : 'http://';
  const loc = integration.location.replace(/\/+$/, '');
  const port = integration.port ? `:${integration.port}` : '';
  // If location already contains a port (host:1234), skip the suffix.
  if (/:\d+$/.test(loc)) {
    return `${proto}${loc}`;
  }
  return `${proto}${loc}${port}`;
};

const toBackendPayload = (integration) => {
  // Resolve the backend registeredServiceId strictly from the SUPPORTED_INTEGRATIONS
  // lookup table — the UI's enginecode (e.g. DELPHIX_MASKING) is a friendly display
  // code and does NOT map to the backend id by simple string transformation.
  let registeredServiceId = integration.registeredServiceId;
  if (!registeredServiceId && integration.enginecode) {
    const match = SUPPORTED_INTEGRATIONS.find(
      (s) => s.enginecode === integration.enginecode
    );
    if (match) registeredServiceId = match.registeredServiceId;
  }
  if (!registeredServiceId) registeredServiceId = 'integrations-delphix';

  const payload = {
    registeredServiceId,
    displayName: integration.name || integration.displayName || '',
    baseUrl: toBaseUrl(integration),
    authType: integration.authType || (integration.username ? 'BASIC' : 'NONE'),
    username: integration.username || null,
    password:
      integration.password == null || integration.password === '****'
        ? null // "don't change" sentinel
        : integration.password,
    enabled: integration.enabled != null ? integration.enabled : true,
  };
  return payload;
};

const fromBackendPayload = (dto) => {
  if (!dto) return null;
  const match = SUPPORTED_INTEGRATIONS.find(
    (s) => s.registeredServiceId === dto.registeredServiceId
  );
  return {
    id: dto.id,
    name: dto.displayName,
    displayName: dto.displayName,
    enginecode: match ? match.enginecode : dto.registeredServiceId,
    enginetype: dto.type || (match ? match.enginetype : null),
    registeredServiceId: dto.registeredServiceId,
    baseUrl: dto.baseUrl,
    location: dto.baseUrl, // legacy alias
    authType: dto.authType,
    username: dto.username,
    password: dto.hasPassword ? '****' : '',
    hasPassword: dto.hasPassword,
    hasToken: dto.hasToken,
    enabled: dto.enabled,
    supportsDataSourceEnumeration: dto.supportsDataSourceEnumeration,
    lastTestAt: dto.lastTestAt,
    lastTestOk: dto.lastTestOk,
    lastTestBody: dto.lastTestBody,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
};

// ---- public API ----

const getEngineOptions = async () => {
  // The new backend does not have a dedicated enumeration endpoint; we return
  // the static list of supported 3rd-party services for this release.
  return {
    integration_options: SUPPORTED_INTEGRATIONS.map((s) => ({
      enginecode: s.enginecode,
      enginetype: s.enginetype,
      name: s.name,
    })),
  };
};

const getIntegrations = async () => {
  const response = await API.get(API_ENDPOINT, INTEGRATIONS_PATH);
  if (!Array.isArray(response)) return [];
  return response.map(fromBackendPayload);
};

/**
 * GET /api/integrations/available — vendor-grouped list of 3rd-party shims the operator has
 * registered with this platform install. Each entry tells the UI which fields to render and
 * whether the current user has already supplied credentials.
 */
const getAvailableIntegrations = async () => {
  const response = await API.get(API_ENDPOINT, `${INTEGRATIONS_PATH}/available`);
  return Array.isArray(response) ? response : [];
};

const getIntegration = async (id) => {
  const response = await API.get(API_ENDPOINT, `${INTEGRATIONS_PATH}/${id}`);
  return fromBackendPayload(response);
};

// The C2API helper does not throw on non-2xx — it returns the error body. Without these
// status checks, a 4xx Save would silently look like success in the UI: the modal closes,
// no toast fires, and the integration is never persisted.
const ensureOk = (response, action) => {
  if (!response || !response.status || response.status >= 400) {
    const status = response && response.status ? response.status : 'no response';
    const serverMsg =
      (response && response.data && (response.data.message || response.data.error)) || null;
    const detail = serverMsg ? `: ${serverMsg}` : '';
    throw new Error(`${action} failed (HTTP ${status})${detail}`);
  }
};

const saveIntegration = async (integration) => {
  const body = toBackendPayload(integration);
  const response = await API.post(API_ENDPOINT, INTEGRATIONS_PATH, { body }, true);
  ensureOk(response, 'Save integration');
  localStorage.setItem('lastConnection', integration.name);
  return fromBackendPayload(response.data);
};

const updateIntegration = async (integration, id) => {
  const body = toBackendPayload(integration);
  const response = await API.put(
    API_ENDPOINT,
    `${INTEGRATIONS_PATH}/${id}`,
    { body },
    true
  );
  ensureOk(response, 'Update integration');
  return fromBackendPayload(response.data);
};

const deleteIntegration = async (id) => {
  return API.del(API_ENDPOINT, `${INTEGRATIONS_PATH}/${id}`);
};

const testConnection = async (integration) => {
  // New flow: if the integration has already been saved, its id is available;
  // we POST /{id}/test which uses stored credentials server-side. If it hasn't
  // been saved yet (creation flow), save first, then test.
  if (integration.id == null || integration.id < 0) {
    const saved = await saveIntegration(integration);
    return testConnectionById(saved.id);
  }
  return testConnectionById(integration.id);
};

const testConnectionById = async (id) => {
  const response = await API.post(
    API_ENDPOINT,
    `${INTEGRATIONS_PATH}/${id}/test`,
    {}
  );
  // Backend returns { ok: boolean, message: string }. Older UI code checks
  // `response.authToken !== "null"` — map to that shape for compatibility.
  return {
    ok: !!(response && response.ok),
    message: response && response.message,
    authToken: response && response.ok ? 'ok' : 'null',
  };
};

const importDataSources = async (id) => {
  const response = await API.post(
    API_ENDPOINT,
    `${INTEGRATIONS_PATH}/${id}/import`,
    {}
  );
  return response; // { jobId, status, registeredServiceId }
};

const getIntegrationJobs = async () => {
  // Retained for signature compatibility; in the new model integration-run jobs
  // are plain orchestration_jobs of type IMPORT. Use the discover-runs API.
  return [];
};

const getJobStatus = async () => {
  // Superseded by the orchestration_jobs polling surface (discover-runs API).
  return {};
};

export {
  saveIntegration,
  updateIntegration,
  deleteIntegration,
  getIntegrations,
  getAvailableIntegrations,
  getIntegration,
  getJobStatus,
  getEngineOptions,
  getIntegrationJobs,
  testConnection,
  testConnectionById,
  importDataSources,
  SUPPORTED_INTEGRATIONS,
};
